import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { enqueueErrorSnackbar } from '../../../actions/Notifications';
import { useShellAppContext } from '../../shell/context/useShellAppContext';
import { notifyOperatorStateRefresh } from '../../home/livePublishEvents';
import { ADMIN_RUNTIME_COMMANDS } from '../../../app/runtime/adminRuntime';

/**
 * Hook for building preview bundles through the selected product runtime.
 *
 * @param {string} shopId - Shop identifier
 * @returns {Object} Build state and methods
 */
export const useBuildPreview = (shopId) => {
  const dispatch = useDispatch();
  const { runtime } = useShellAppContext();
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState(null);
  const [buildSuccess, setBuildSuccess] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => () => { cancelledRef.current = true; }, []);

  const extractErrorMessage = useCallback((err) => {
    if (!err.message) {
      return 'Failed to build preview. Please try again or contact support.';
    }

    // Extract user-friendly message (after last colon, without URL and technical details)
    if (err.message.includes('Build service is temporarily unavailable')) {
      return 'Build service is temporarily unavailable. Please try again later.';
    }

    if (err.message.includes('runtime is not available')) {
      return 'Preview build is not available in this admin environment.';
    }

    // Extract the segment after the last colon, avoiding raw status codes and URL fragments.
    const lastColon = err.message.lastIndexOf(':');
    if (lastColon !== -1) {
      const candidate = err.message.slice(lastColon + 1).trim();
      if (candidate && candidate.length >= 3 && !/^\d{3}$/.test(candidate) && !/^\//.test(candidate)) {
        return candidate;
      }
    }

    if (err.message.length >= 8 && !/^https?:\/\//i.test(err.message)) {
      return err.message;
    }

    return 'Failed to build preview. Please try again or contact support.';
  }, []);

  const build = useCallback(async () => {
    cancelledRef.current = false;
    setBuilding(true);
    setBuildError(null);
    setBuildSuccess(false);

    try {
      if (!runtime || typeof runtime.sendMessageWithResponse !== 'function') {
        throw new Error('Admin runtime is not available.');
      }
      const response = await runtime.sendMessageWithResponse(
        ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW,
        { shopId },
      );
      if (cancelledRef.current) return { success: false, error: 'cancelled' };
      if (response?.success === false) {
        throw new Error(response.error || 'Preview build failed.');
      }
      setBuildSuccess(true);
      notifyOperatorStateRefresh();
      return { success: true };
    } catch (err) {
      console.error('Build failed:', err);
      if (!cancelledRef.current) {
        const errorMessage = extractErrorMessage(err);
        setBuildError(errorMessage);
        dispatch(enqueueErrorSnackbar(errorMessage));
        return { success: false, error: errorMessage };
      }
      return { success: false, error: 'cancelled' };
    } finally {
      if (!cancelledRef.current) setBuilding(false);
    }
  }, [
    shopId,
    runtime,
    extractErrorMessage,
    dispatch,
  ]);

  const resetBuild = useCallback(() => {
    setBuilding(false);
    setBuildError(null);
    setBuildSuccess(false);
  }, []);

  return {
    build,
    building,
    buildError,
    buildSuccess,
    resetBuild,
  };
};
